import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CalendarDays, Plus, Trash2, RefreshCw, Info } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { listHolidays, addHoliday, deleteHoliday, seedHolidays } from '../../services/taskApi';

const FlowHolidays = () => {
  const { isDark } = useOutletContext();
  const [holidays, setHolidays] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: '', name: '' });
  const [submitting, setSubmitting] = useState(false);

  const cardCls = isDark ? 'bg-[#0f1729] border-white/10' : 'bg-white border-gray-200';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listHolidays();
      setPolicy(res.data.policy || null);
      setHolidays(res.data.holidays || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load holidays');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.date || !form.name.trim()) { toast.error('Date and name are required'); return; }
    setSubmitting(true);
    try {
      const res = await addHoliday({ date: form.date, name: form.name.trim() });
      toast.success(res.data.message || 'Holiday saved');
      setForm({ date: '', name: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete holiday "${name}"?`)) return;
    try {
      await deleteHoliday(id);
      toast.success('Deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSeed = async () => {
    try {
      const res = await seedHolidays();
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Group holidays by year for cleaner display
  const grouped = holidays.reduce((acc, h) => {
    const y = h.date.slice(0, 4);
    if (!acc[y]) acc[y] = [];
    acc[y].push(h);
    return acc;
  }, {});
  const years = Object.keys(grouped).sort();

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <CalendarDays size={20} /> Holiday Calendar
          </h1>
          <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            These dates are excluded when SLA breach &amp; TAT calculations measure working hours.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={fetchData} className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''} data-testid="holidays-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {policy && (
        <Card className={cardCls}>
          <CardContent className="p-4 flex items-start gap-3">
            <Info size={16} className="text-blue-400 mt-0.5 shrink-0" />
            <div className="text-xs leading-relaxed">
              <div className={isDark ? 'text-white/70' : 'text-gray-700'}>
                <b>Working window:</b> {policy.working_hours} &nbsp;·&nbsp; <b>Working days:</b> {policy.working_days}
              </div>
              <div className={`mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                SLA breach alerts (2h / 6h / 12h / 24h) count <b>business hours only</b> — nights, Sundays and the dates below are skipped.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add new holiday */}
      <Card className={cardCls}>
        <CardContent className="p-4">
          <h2 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Add Holiday</h2>
          <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-3">
              <Label className={isDark ? 'text-white/60' : ''}>Date *</Label>
              <Input type="date" className={`mt-1 ${inputCls}`} value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                required min={today.slice(0, 4) + '-01-01'} data-testid="holiday-date-input" />
            </div>
            <div className="sm:col-span-7">
              <Label className={isDark ? 'text-white/60' : ''}>Name *</Label>
              <Input className={`mt-1 ${inputCls}`} value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Diwali, Company-specific holiday" maxLength={80} required data-testid="holiday-name-input" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={submitting} className="w-full h-9 bg-blue-600 hover:bg-blue-700" data-testid="holiday-add-btn">
                <Plus size={14} className="mr-1.5" /> {submitting ? 'Saving…' : 'Add'}
              </Button>
            </div>
          </form>
          <p className={`mt-2 text-[11px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
            Re-adding the same date updates its name. Tip: re-import the standard list anytime using <button type="button" onClick={handleSeed} className={`underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`} data-testid="holiday-seed-btn">Seed default Indian holidays</button>.
          </p>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className={`h-10 rounded animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div>
      ) : holidays.length === 0 ? (
        <Card className={cardCls}><CardContent className="p-8 text-center">
          <CalendarDays size={36} className={`mx-auto mb-2 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-white/40' : 'text-gray-500'}>No holidays configured yet</p>
        </CardContent></Card>
      ) : (
        years.map(year => (
          <Card key={year} className={cardCls} data-testid={`holiday-year-${year}`}>
            <CardContent className="p-4">
              <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{year} · {grouped[year].length} holidays</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {grouped[year].map(h => {
                  const isPast = h.date < today;
                  return (
                    <div key={h.id} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'} ${isPast ? 'opacity-60' : ''}`}>
                      <div className="min-w-0">
                        <div className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{h.name}</div>
                        <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}{h.seed ? ' · default' : ''}</div>
                      </div>
                      <button onClick={() => handleDelete(h.id, h.name)}
                        className={`p-1.5 rounded hover:bg-red-500/10 ${isDark ? 'text-red-400' : 'text-red-600'}`}
                        title="Delete"
                        data-testid={`holiday-delete-${h.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default FlowHolidays;
